import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import type { U2000AutoCompleteSearchResult } from '../u2000Core/types/U2000AutoCompleteSearchResult';
import { u2000ExtractAutoCompleteResults } from '../u2000Core/utils/u2000ExtractAutoCompleteResults';
import {
  doFindRequestedLot,
  luHandleAppiError,
  type U3141AutoCompletCriterias,
  type U3141FindRequestedLotItem,
} from './U3141Service';
import { NS } from './U3141';

/** Recherche lot demandé via POST /U3141/findRequestedLot (handlers obligatoires). */
export function useU3141FindRequestedLotHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3141AutoCompletCriterias): Promise<U2000AutoCompleteSearchResult<U3141FindRequestedLotItem>> => {
      try {
        const response = await doFindRequestedLot(axios, criterias);
        const extracted = u2000ExtractAutoCompleteResults<{ requestedLot?: string }>(response.data);
        const results = extracted.results
          .map((row) => {
            const lot = row?.requestedLot?.trim() ?? '';
            if (!lot) return null;
            return { id: lot, requestedLot: lot } satisfies U3141FindRequestedLotItem;
          })
          .filter((row): row is { id: string; requestedLot: string } => row != null);
        return { ...extracted, results };
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}
